import { resolveEffectiveRolId, resolveIsSuperAdminView, usesTargetRole } from './effective-identity';
import type { EffectiveIdentityInput } from './types';

/**
 * Permisos de pantalla en "Vista del usuario" (vista fiel). Funciones PURAS, sin
 * React ni Supabase: el hook `usePortalPagePermissions` solo les pasa datos.
 *
 * Por qué existe: `usePagePermissions` resuelve los flags con el rol de la SESIÓN.
 * En vista fiel hay que pintar los del rol IMPERSONADO, pero sin tocar la
 * autorización de rutas (`useAllowedMenus` / `PermissionRoute`): la vista fiel
 * simula lo que el portal PINTA, nunca lo que el usuario tiene derecho a ABRIR.
 *
 * LÍMITE (documentado a propósito): las consultas siguen viajando con el token del
 * admin. Todo recorte que viva solo en RLS o dentro de una RPC (p. ej. la bandeja
 * de cuentas, que responde 403 a quien no tiene permiso, o "mis tickets", que se
 * filtra por la sesión) NO se reproduce aquí. Para eso la prueba fiel es entrar
 * con la cuenta del usuario.
 */

/**
 * Catálogo único flag → `permisos.nombre`. Debe ser idéntico al mapeo de
 * `usePagePermissions` (un test lee su fuente y falla si se desincronizan): así
 * un flag nuevo que se agregue allá obliga a declararlo aquí.
 *
 * Un flag que `usePagePermissions` devuelva y no esté aquí se resuelve en `false`
 * en vista fiel (y además cuenta como escritura): nunca se abre una acción por
 * omisión.
 */
export const PAGE_PERMISSION_NAME_BY_FLAG = {
  canRead: 'leer',
  canCreate: 'crear',
  canUpdate: 'actualizar',
  canDelete: 'eliminar',
  canApprove: 'aprobar',
  canExport: 'exportar',
  canGenerateOffer: 'generar_oferta',
  canGenerateDigitalOffer: 'generar_oferta_digital',
  canChangeEmail: 'cambiar_correo',
  canReturnPayment: 'devolver_pago',
  canConsolidatePayment: 'consolidar_pago',
  canRegisterPayment: 'registrar_pago',
  canTransferPayment: 'transferir_pago',
  canEditAccount: 'editar_cuenta',
  canResetFinancing: 'reiniciar_financiamiento',
  canAdjustPaymentPlan: 'ajustar_plan_pagos',
  canManageFines: 'gestionar_multas',
  canGenerateAccountStatement: 'generar_estado_cuenta',
  canUploadAccountDocument: 'subir_documento_cuenta',
  canMarkSpecialStatus: 'marcar_estatus_especial',
  canIntegrateFinalPayment: 'integrar_pago_final',
  canEscrituraYEntrega: 'escritura_y_entrega',
  canConnectNotificationPhone: 'conectar_telefono_notificaciones',
  canConfigureSoloApps: 'configurar_solo_apps',
  canDeriveToBank: 'derivar_a_banco',
  canAbsorbBalance: 'absorber_saldo',
  canDacionEnPago: 'registrar_dacion_en_pago',
  canCorrectContractValidation: 'corregir_validacion_contrato',
  canDisperse: 'dispersar',
  canEditClosedAccount: 'editar_cuenta_cerrada',
  canApproveEvidenceException: 'aprobar_excepcion_evidencia',
  canManageContactChat: 'gestionar_contacto_chat',
  canSeeAll: 'ver_todos',
} as const satisfies Record<string, string>;

export type KnownPageFlag = keyof typeof PAGE_PERMISSION_NAME_BY_FLAG;

/**
 * Flags que NO escriben datos de negocio y por eso se respetan tal cual en vista
 * fiel: ver la pantalla, ver la pestaña Todos, exportar y generar el PDF del estado de cuenta (genera un
 * archivo, no cambia la cuenta). Todo lo demás —incluido cualquier flag nuevo— es
 * escritura y se apaga.
 */
export const NON_WRITE_FLAGS: ReadonlySet<string> = new Set<KnownPageFlag>([
  'canRead',
  'canExport',
  'canGenerateAccountStatement',
  'canSeeAll',
]);

/** Etiquetas para el aviso "en esta pantalla podría…". */
export const PAGE_FLAG_LABELS: Record<KnownPageFlag, string> = {
  canRead: 'Ver',
  canCreate: 'Crear',
  canUpdate: 'Editar',
  canDelete: 'Eliminar',
  canApprove: 'Aprobar',
  canExport: 'Exportar',
  canGenerateOffer: 'Generar oferta PDF',
  canGenerateDigitalOffer: 'Generar oferta digital',
  canChangeEmail: 'Cambiar correo',
  canReturnPayment: 'Marcar pago devuelto',
  canConsolidatePayment: 'Conciliar pago',
  canRegisterPayment: 'Registrar pago',
  canTransferPayment: 'Transferir pago',
  canEditAccount: 'Editar cuenta',
  canResetFinancing: 'Reiniciar financiamiento',
  canAdjustPaymentPlan: 'Ajustar plan de pagos',
  canManageFines: 'Gestionar multas',
  canGenerateAccountStatement: 'Estado de cuenta',
  canUploadAccountDocument: 'Subir documentos',
  canMarkSpecialStatus: 'Marcar o quitar Rescisión (Extraordinario en Productos)',
  canIntegrateFinalPayment: 'Integrar mensualidades al pago final',
  canEscrituraYEntrega: 'Escritura y entrega',
  canConnectNotificationPhone: 'Conectar teléfono de notificaciones',
  canConfigureSoloApps: 'Marcar pipelines como solo apps',
  canDeriveToBank: 'Derivar a banco',
  canAbsorbBalance: 'Absorber saldo',
  canDacionEnPago: 'Registrar y corregir dación en pago',
  canCorrectContractValidation: 'Corregir validación de contrato',
  canDisperse: 'Dispersar comisiones por STP',
  canEditClosedAccount: 'Editar cuenta cerrada (propiedad entregada)',
  canApproveEvidenceException: 'Aprobar evidencia por excepción',
  canManageContactChat: 'Ver chats y ficha del contacto',
  canSeeAll: 'Ver la pestaña Todos',
};

export function isWriteFlag(flag: string): boolean {
  return !NON_WRITE_FLAGS.has(flag);
}

export function pageFlagLabel(flag: string): string {
  return (PAGE_FLAG_LABELS as Record<string, string>)[flag] ?? flag.replace(/^can/, '');
}

/** Llaves `can*` booleanas de un objeto de permisos (p. ej. lo que devuelve `usePagePermissions`). */
export function pageFlagKeys(permissions: object): string[] {
  return Object.entries(permissions)
    .filter(([key, value]) => /^can[A-Z]/.test(key) && typeof value === 'boolean')
    .map(([key]) => key);
}

/** Flags concedidos a partir de los nombres de permiso del rol. Desconocido → false. */
export function flagsFromPermissionNames(
  flagKeys: readonly string[],
  permissionNames: ReadonlySet<string>
): Record<string, boolean> {
  const catalog = PAGE_PERMISSION_NAME_BY_FLAG as Record<string, string>;
  return Object.fromEntries(
    flagKeys.map((flag) => [flag, flag in catalog ? permissionNames.has(catalog[flag]) : false])
  );
}

export interface FielPagePermissionsInput {
  /** Llaves de flags a resolver (las de `usePagePermissions`, para que uno nuevo se propague solo). */
  flagKeys: readonly string[];
  /** `permisos.nombre` activos del rol impersonado en el submenú de la pantalla. */
  permissionNames: ReadonlySet<string>;
  /** ¿El impersonado es Super Administrador? Entonces tendría todo (como en `usePagePermissions`). */
  targetIsSuperAdmin: boolean;
}

export interface FielPagePermissions {
  /** Lo que el impersonado TIENE en esta pantalla (para el aviso). */
  granted: Record<string, boolean>;
  /** Lo que se pinta: lo suyo, con toda escritura apagada (edición bloqueada). */
  effective: Record<string, boolean>;
  /** Acciones de escritura que tendría y que esta vista oculta. */
  blockedWriteFlags: string[];
}

export function resolveFielPagePermissions(input: FielPagePermissionsInput): FielPagePermissions {
  const { flagKeys, permissionNames, targetIsSuperAdmin } = input;
  const granted = targetIsSuperAdmin
    ? Object.fromEntries(flagKeys.map((flag) => [flag, true]))
    : flagsFromPermissionNames(flagKeys, permissionNames);
  const effective = Object.fromEntries(
    flagKeys.map((flag) => [flag, granted[flag] === true && !isWriteFlag(flag)])
  );
  const blockedWriteFlags = flagKeys.filter((flag) => granted[flag] && isWriteFlag(flag));
  return { granted, effective, blockedWriteFlags };
}

/**
 * Solo al impersonar ("Ver como"): flags para PINTAR las acciones que el impersonado tiene,
 * aunque la vista sea de solo lectura. Las acciones se ven y no se ejecutan (la pantalla las
 * frena con un aviso); el permiso real lo decide el servidor con la sesión. Sin "Ver como"
 * (`readOnlyView` en false) devuelve los permisos tal cual.
 */
export function conAccionesVisiblesVerComo<T extends { readOnlyView: boolean; blockedWriteFlags?: readonly string[] }>(p: T): T {
  if (!p.readOnlyView || !p.blockedWriteFlags?.length) return p;
  return { ...p, ...Object.fromEntries(p.blockedWriteFlags.map((flag) => [flag, true])) };
}

/**
 * Prefijos de los portales donde la vista fiel de permisos está conectada. Fuera
 * de ellos el hook delega SIEMPRE en `usePagePermissions`: un componente
 * compartido montado en otro portal no hereda una impersonación que quedó viva en
 * memoria.
 */
export const FIEL_PAGE_PERMISSION_SCOPES = ['/admin/portal-cobranza', '/admin/portal-productos'] as const;

export function isInFielScope(
  pathname: string,
  scopes: readonly string[] = FIEL_PAGE_PERMISSION_SCOPES
): boolean {
  return scopes.some((scope) => pathname === scope || pathname.startsWith(scope + '/'));
}

/**
 * Con qué rol se pinta el MENÚ de un portal.
 *   - `simulated`: impersonando con rol publicado → rol del impersonado.
 *   - `showAll`: el rol efectivo ve todos los submenús activos sin filtrar
 *     (`fullNavRoleIds`, p. ej. {1, 2} en Cobranza).
 *   - `rolId`: rol con el que filtrar `submenus_permisos` cuando no es `showAll`.
 * Sin impersonar es el rol de la sesión. Impersonando sin rol publicado, `rolId`
 * es null y no se concede nada (nunca se cae al rol del admin).
 */
export function resolvePortalNavRole(
  input: EffectiveIdentityInput,
  fullNavRoleIds: ReadonlySet<number> = new Set()
): { simulated: boolean; rolId: number | null; showAll: boolean } {
  const simulated = usesTargetRole(input.target);
  const rolId = resolveEffectiveRolId(input);
  const showAll =
    (rolId != null && fullNavRoleIds.has(rolId)) || (simulated && resolveIsSuperAdminView(input));
  return { simulated, rolId, showAll };
}
